"use client"

import type React from "react"

/* eslint-disable @typescript-eslint/no-explicit-any */
import Funil from "@/assets/icons/funil"
import { useGetAccountingAccountsActiveQuery } from "@/services/account-accouting"
import { numberToCurrency } from "@/utils/money"
import type { ExtendedColumnDef } from "components/Datatable"
import { Checkbox } from "components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select"
import { format } from "date-fns"
import { type SetStateAction } from "react"
import { Controller, FormProvider, type FieldValues, type UseFormReturn } from "react-hook-form"
import type { IDivergence } from "src/@types/divergence"
import SelectCellJustification from "../components/divergence-list/components/select-cell-justification"
import { useDivergenceFilterActions } from "../components/divergence-list/ducks/filter/hooks/actions"
import { ConciliatedDivergencesPopover } from "./conciliated-popover"
import DivergenceHistoryPopover from "./divergence-history"

export const useColumns = (
  methods: UseFormReturn<FieldValues, any, undefined>,
  errors: { [field: string]: string } | undefined,
  showCheckboxes: boolean,
  selectedItems: Set<string>,
  setSelectedItems: React.Dispatch<SetStateAction<Set<string>>>,
) => {
  const { data: accountingAccounts } = useGetAccountingAccountsActiveQuery()

  const { control, setValue } = methods;
  const { updateFilter } = useDivergenceFilterActions();

  const columns: ExtendedColumnDef<IDivergence>[] = [
    ...(showCheckboxes
      ? [
        {
          accessorKey: "conciliation",
          header: "Conciliar",
          sticky: "left-0" as const,
          cell: ({ row }: { row: { original: IDivergence } }) => {
            const values = row.original
            const isChecked = selectedItems.has(values.id.toString())
            const handleCheckboxChange = (checked: boolean) => {
              setSelectedItems((prev) => {
                const newSet = new Set(prev)
                checked ? newSet.add(values.id.toString()) : newSet.delete(values.id.toString())
                return newSet
              })
            }

            return (
              <div className="w-full flex items-center justify-center pr-2">
                <Checkbox checked={isChecked} onCheckedChange={handleCheckboxChange} />
              </div>
            )
          },
        },
      ]
      : []),
    {
      accessorKey: "operator",
      header: "Operador",
      cell: ({ row }) => {
        const values = row.original
        return <div>{values.operator}</div>
      },
    },
    {
      accessorKey: "description",
      header: "Descrição da Divergência",
      cell: ({ row }) => {
        const values = row.original
        return <div>{values.name}</div>
      },
    },
    {
      id: "Justificativa",
      header: () => (
        <div
          className="flex w-full items-center"
          onClick={() => {
            updateFilter({
              key: "popoverFilterJustification",
              value: true,
            });
          }}
        >
          <span>Justificativa</span>
          <Funil color="black" size={16} />
        </div>
      ),
      cell: ({ row }: { row: any }) => {
        const justificationId =
          row.original.justification_id;

        return (
          <div 
            className="min-w-[240px] max-w-[320px]" 
            data-needs-justification={row.original.id}
          >
            <FormProvider {...methods}>
              <form>
                <SelectCellJustification
                  control={control}
                  justification={justificationId ?? ""}
                  rowId={row.original.id}
                />
              </form>
            </FormProvider>
          </div>
        );
      },
    },

    {
      accessorKey: "value",
      header: "Valor",
      cell: ({ row }) => {
        const values = row.original
        return <div className="min-w-20">{numberToCurrency(values.amount)}</div>
      },
      className: "disabled-column",
    },
    {
      accessorKey: "conciliation_date",
      header: "Data",
      sticky: "left-0" as const, 
      cell: ({ row }) => {
        const values = row.original
        return <div className="min-w-20">{format(new Date(values.conciliation_date), "dd/MM/yyyy")}</div>
      },
      className: "disabled-column",
    },
    {
      accessorKey: "divergence-conciliation",
      header: "Valor Abatido com:",
      cell: ({ row }) => {
        const values = row.original
        const hasConciliatedDivergence =
          Array.isArray(values.conciliatedDivergence?.divergences) &&
          values.conciliatedDivergence.divergences.length > 1
        const conciliatedDivergences =
          hasConciliatedDivergence && Array.isArray(values.conciliatedDivergence.divergences)
            ? values.conciliatedDivergence.divergences.filter((divergence) => divergence.id !== row.original.id)
            : []

        const highlightRows = () => {
          if (values.conciliatedDivergence && Array.isArray(values.conciliatedDivergence.divergences)) {
            document.querySelectorAll(`tr[data-conciliation-id="${values.conciliatedDivergence.id}"]`).forEach((el) => {
              el.classList.add("bg-gray-50", "border-l-4", "border-l-[#402430]")
            })
          }
        }

        const unhighlightRows = () => {
          if (values.conciliatedDivergence && Array.isArray(values.conciliatedDivergence.divergences)) {
            document.querySelectorAll(`tr[data-conciliation-id="${values.conciliatedDivergence.id}"]`).forEach((el) => {
              el.classList.remove("bg-gray-50", "border-l-4", "border-l-[#402430]")
            })
          }
        }

        return (
          <div className="flex w-full" data-conciliation-id={values.conciliatedDivergence?.id}>
            {hasConciliatedDivergence ? (
              <ConciliatedDivergencesPopover
                values={values}
                conciliatedDivergences={conciliatedDivergences}
                highlightRows={highlightRows}
                unhighlightRows={unhighlightRows}
              />
            ) : (
              <span className="text-gray-400 text-sm">—</span>
            )}
          </div>
        )
      },
      className: "disabled-column",
    },
    {
      id: "hasDeposit",
      header: () => <div className="flex w-full align-center justify-start">Conta Contábil</div>,
      cell: ({ row }) => {
        const values = row.original
        const { justification_account_id, justification_id } = values
        const defaultValue = accountingAccounts?.find(
          (ac) => Number(ac.id) === Number(justification_account_id || justification_id || 0),
        )

        return (
          <div className="flex align-center justify-center w-[170px] checkbox px-0 mx-0">
            <FormProvider {...methods}>
              <form>
                <div className="w-full flex items-center justify-center">
                  <Controller
                    name={`divergence_${row.original.id}.accounting`}
                    control={control}
                    defaultValue={defaultValue?.id?.toString()}
                    render={({ field }) => {
                      return (
                        <Select
                          onValueChange={(value) => {
                            setValue(`divergence_${row.original.id}.accounting`, value)
                          }}
                          value={field.value}
                        >
                          <SelectTrigger className="w-full border-none">
                            <SelectValue placeholder="Selecione um item" className="w-full" />
                          </SelectTrigger>
                          <SelectContent>
                            {accountingAccounts?.map((item) => (
                              <SelectItem key={item.id} value={item.id.toString()}>
                                {item.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )
                    }}
                  />
                </div>
              </form>
            </FormProvider>
          </div>
        )
      },
      className: "w-[200px] mx-0 px-0",
    },
    {
      id: "conferred",
      header: () => (
        <div className="flex w-full align-center justify-start">
          Observações <span className="text-[#d0312d] ml-[3px]">*</span>
        </div>
      ),
      cell: ({ row }) => {
        setValue(`divergence_${row.original.id}.version`, row.original.version)
        setValue(`divergence_${row.original.id}.id`, row.original.id)
        const values = row.original
        const errorMsg = errors ? errors[`observation_${row.original.id}`] : undefined

        return (
          <div className={`flex align-center justify-center w-full h-full input-container min-w-[170px] max-w-[300px]: `}>
            <FormProvider {...methods}>
              <form>
                <div className="w-full h-full flex items-center justify-center">
                  <Controller
                    name={`divergence_${row.original.id}.observation`}
                    control={control}
                    defaultValue={values.account_notes}
                    render={({ field }) => {
                      return (
                        <input
                          type="text"
                          onChange={(value) => {
                            setValue(`divergence_${row.original.id}.observation`, value.target.value)
                          }}
                          value={field.value}
                          placeholder="Escreva sua observação"
                          className={`bg-transparent w-full h-full ${errorMsg ? "has-error" : ""}`}
                        />
                      )
                    }}
                  />
                </div>
              </form>
            </FormProvider>
          </div>
        )
      },
      className: "form-column",
    },
    {
      id: "history",
      header: () => <div className="flex w-full align-center justify-center">Histórico</div>,
      cell: ({ row }) => {
        const values = row.original;
        return (
          <div className="flex items-center justify-center w-full">
            <DivergenceHistoryPopover divergenceId={+values.id} />
          </div>
        );
      },
      className: "history-column",
    },
  ]

  return columns
}
